/**
 * useCaseCatalog — loads the manifest-driven case catalog.
 *
 * The catalog is built from MyMDO-DAFoam/cases/<Name>/mymdo.json by the DAFoam
 * pipeline and served by GET /api/simulations/cases/. Every consumer of this
 * hook (launch page, detail page) therefore picks up a new case automatically:
 * its launch cards, its input form, its cost estimate and its results view all
 * come from the manifest.
 *
 * The catalog is small and changes only on a DAFoam deploy, so it is cached for
 * the lifetime of the tab and shared across components.
 */
import { useCallback, useEffect, useState } from 'react';
import simulationService from '../services/simulationService';

// Frozen module-level constants: consumers put `cases` in useCallback/useMemo
// dependency arrays, so returning a fresh [] on each render would re-create
// their callbacks every time and spin the component in a render loop.
const NO_CASES = Object.freeze([]);
const NO_TEMPLATES = Object.freeze([]);
const EMPTY = { cases: NO_CASES, templates: NO_TEMPLATES, source: null, generated_at: null };

// Module-level cache: one fetch per page load, shared by every consumer.
let cachedCatalog = null;
let inFlight = null;

const fetchCatalog = (force = false) => {
  if (force) {
    cachedCatalog = null;
    inFlight = null;
  }
  if (cachedCatalog) return Promise.resolve(cachedCatalog);
  if (!inFlight) {
    inFlight = simulationService
      .getCaseCatalog()
      .then((data) => {
        cachedCatalog = { ...EMPTY, ...data };
        return cachedCatalog;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

const useCaseCatalog = () => {
  const [catalog, setCatalog] = useState(cachedCatalog || EMPTY);
  const [loading, setLoading] = useState(!cachedCatalog);
  const [error, setError] = useState(null);

  const load = useCallback((force = false) => {
    let cancelled = false;
    setLoading(true);
    fetchCatalog(force)
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setError(null);
        }
      })
      .catch((err) => {
        // A catalog failure must not take the page down: the caller keeps its
        // built-in workflows and simply shows no manifest-driven case.
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  return {
    cases: catalog.cases || NO_CASES,
    templates: catalog.templates || NO_TEMPLATES,
    source: catalog.source,
    generatedAt: catalog.generated_at,
    loading,
    error,
    reload: () => load(true),
  };
};

/** Find a case by its id (= workflow_type on a simulation). */
export const findCase = (cases, workflowType) =>
  (cases || []).find((c) => c.id === workflowType) || null;

/**
 * Estimated cost of a run, from the manifest variant matching its analysis mode.
 * Returns null when the case declares none, so callers can fall back.
 */
export const estimatedCostFromCatalog = (cases, workflowType, analysisMode) => {
  const found = findCase(cases, workflowType);
  if (!found) return null;
  const variant = (found.variants || []).find((v) => v.analysis_mode === analysisMode);
  const cost = variant?.cost_usd;
  return typeof cost === 'number' ? cost : null;
};

export default useCaseCatalog;
